import { useController, useFormContext } from "react-hook-form";
import {
  Box,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Typography,
  Grid,
} from "@mui/material";
import Checkbox from "../../../Form/Checkbox";
import Accordion from "../../../Containers/Accordion";
import { useState } from "react";
import {
  DEVICES_PLACEHOLDER,
  META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE,
  META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE_DESCRIPTION,
  META_ADGROUP_PLACEMENTS_TYPE_MANUAL,
  META_ADGROUP_PLACEMENTS_TYPE_MANUAL_DESCRIPTION,
  PLACEMENTS_PLACEHOLDER,
  PLATFORMS_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../../utils/colors";
import {
  PLACEMENT_GROUPS,
  PLATFORMS,
} from "../../../../utils/catalogs/metaPlacements";

const DEVICES = [
  { key: "mobile", label: "Mobile" },
  { key: "desktop", label: "Desktop" },
];

const FORM_CONTROL_CHECKBOX_CORE_SX = {
  m: 0,
  mx: 0,
  gap: 0.25,
  minWidth: 0,
  alignItems: "center",
  "& .MuiCheckbox-root": {
    flexShrink: 0,
    alignSelf: "center",
    marginInlineEnd: 0,
  },
};

const FORM_CONTROL_ROW_CHECKBOX_SX = {
  ...FORM_CONTROL_CHECKBOX_CORE_SX,
  display: "inline-flex",
  maxWidth: "100%",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    maxWidth: "100%",
    flex: "0 1 auto",
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const FORM_CONTROL_STACK_CHECKBOX_SX = {
  ...FORM_CONTROL_CHECKBOX_CORE_SX,
  mb: 0.75,
  display: "flex",
  width: "100%",
  "& .MuiFormControlLabel-label": {
    minWidth: 0,
    maxWidth: "100%",
    flex: "1 1 auto",
    lineHeight: 1.35,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
  "&:last-of-type": {
    mb: 0,
  },
};

const formattedFormToComponent = (formValue) => {
  return {
    type: formValue.type || "advantage",
    devices: formValue.devicePlatforms || [],
    platforms: formValue.publisherPlatforms || [],
    placements: [
      ...(formValue.facebookPositions || []),
      ...(formValue.instagramPositions || []),
      ...(formValue.messengerPositions || []),
      ...(formValue.audienceNetworkPositions || []),
    ],
  };
};

const formattedComponentToForm = (local) => {
  return {
    type: local.type,
    devicePlatforms: local.devices,
    publisherPlatforms: local.platforms,
    facebookPositions: local.placements.filter((key) =>
      PLACEMENT_GROUPS.some((group) =>
        group.placements.some((p) => p.key === key && p.platform === "facebook")
      )
    ),
    instagramPositions: local.placements.filter((key) =>
      PLACEMENT_GROUPS.some((group) =>
        group.placements.some(
          (p) => p.key === key && p.platform === "instagram"
        )
      )
    ),
    messengerPositions: local.placements.filter((key) =>
      PLACEMENT_GROUPS.some((group) =>
        group.placements.some(
          (p) => p.key === key && p.platform === "messenger"
        )
      )
    ),
    audienceNetworkPositions: local.placements.filter((key) =>
      PLACEMENT_GROUPS.some((group) =>
        group.placements.some(
          (p) => p.key === key && p.platform === "audienceNetwork"
        )
      )
    ),
  };
};

export const AdGroupPlacementsMetaForm = ({
  control,
  name,
  disabled = false,
}) => {
  const {
    field: { value },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: {
      type: "advantage",
      devices: [],
      platforms: [],
    },
  });

  const { setValue } = useFormContext();
  const { t } = useTranslationApp();
  const [localState, setLocalState] = useState(() =>
    formattedFormToComponent(value)
  );

  const syncWithForm = (nextState) => {
    setLocalState(nextState);
    setValue(name, formattedComponentToForm(nextState), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onPlacementTypeChange = (e) => {
    const value = e.target.value;
    let nextState = { ...localState, type: value };
    if (value === "manual") {
      nextState = {
        ...nextState,
        devices: DEVICES.map((d) => d.key),
        platforms: PLATFORMS.map((p) => p.key),
        placements: PLACEMENT_GROUPS.flatMap((group) =>
          group.placements.map((p) => p.key)
        ),
      };
    }
    syncWithForm(nextState);
  };

  const onDeviceChange = (deviceKey, checked) => {
    let newDevices = checked
      ? [...localState.devices, deviceKey]
      : localState.devices.filter((d) => d !== deviceKey);
    syncWithForm({ ...localState, devices: newDevices });
  };

  const onPlatformChange = (platformKey, checked) => {
    let newPlatforms = checked
      ? [...localState.platforms, platformKey]
      : localState.platforms.filter((p) => p !== platformKey);
    // Sync placements
    const platformPlacements = PLACEMENT_GROUPS.flatMap((group) =>
      group.placements
        .filter((p) => p.platform === platformKey)
        .map((p) => p.key)
    );
    let newPlacements = checked
      ? Array.from(new Set([...localState.placements, ...platformPlacements]))
      : localState.placements.filter((k) => !platformPlacements.includes(k));
    syncWithForm({
      ...localState,
      platforms: newPlatforms,
      placements: newPlacements,
    });
  };

  const onGroupPlacementsChange = (group, checked) => {
    const visiblePlacements = group.placements.filter((p) =>
      localState.platforms.includes(p.platform)
    );
    const groupPlacementKeys = visiblePlacements.map((p) => p.key);
    if (groupPlacementKeys.length === 0) {
      return;
    }
    let newPlacements = checked
      ? Array.from(new Set([...localState.placements, ...groupPlacementKeys]))
      : localState.placements.filter((k) => !groupPlacementKeys.includes(k));
    // Sync platforms
    const groupPlatforms = Array.from(
      new Set(visiblePlacements.map((p) => p.platform))
    );
    let currentPlatforms = localState.platforms;
    let newPlatforms = checked
      ? Array.from(new Set([...currentPlatforms, ...groupPlatforms]))
      : currentPlatforms.filter((p) => {
          const stillHasPlacement = PLACEMENT_GROUPS.flatMap(
            (g) => g.placements
          )
            .filter((pl) => pl.platform === p)
            .some((pl) => newPlacements.includes(pl.key));
          return stillHasPlacement;
        });
    syncWithForm({
      ...localState,
      platforms: newPlatforms,
      placements: newPlacements,
    });
  };

  const onPlacementChange = (placement, checked) => {
    let newPlacements = checked
      ? [...localState.placements, placement.key]
      : localState.placements.filter((k) => k !== placement.key);
    // Sync platforms
    let currentPlatforms = localState.platforms;
    if (checked) {
      if (!currentPlatforms.includes(placement.platform)) {
        currentPlatforms = [...currentPlatforms, placement.platform];
      }
    } else {
      const stillHasPlacement = PLACEMENT_GROUPS.flatMap((g) => g.placements)
        .filter((pl) => pl.platform === placement.platform)
        .some((pl) => newPlacements.includes(pl.key));
      if (!stillHasPlacement) {
        currentPlatforms = currentPlatforms.filter(
          (p) => p !== placement.platform
        );
      }
    }
    syncWithForm({
      ...localState,
      platforms: currentPlatforms,
      placements: Array.from(new Set(newPlacements)),
    });
  };

  return (
    <Box>
      <FormControl component="fieldset">
        <RadioGroup value={localState.type} onChange={onPlacementTypeChange}>
          <FormControlLabel
            value="advantage"
            control={<Radio disabled={disabled} />}
            label={
              <Box>
                {t(META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE)}
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                  {t(META_ADGROUP_PLACEMENTS_TYPE_ADVANTAGE_DESCRIPTION)}
                </Box>
              </Box>
            }
          />
          <FormControlLabel
            value="manual"
            control={<Radio disabled={disabled} />}
            label={
              <Box>
                {t(META_ADGROUP_PLACEMENTS_TYPE_MANUAL)}
                <Box sx={{ fontSize: 12, color: "text.secondary" }}>
                  {t(META_ADGROUP_PLACEMENTS_TYPE_MANUAL_DESCRIPTION)}
                </Box>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {localState.type === "manual" && (
        <Box mt={3}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            {t(DEVICES_PLACEHOLDER)}
          </Typography>
          <FormGroup
            row
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            {DEVICES.map((device) => {
              const isChecked = localState.devices.includes(device.key);
              const selectedCount = localState.devices.length;
              const isDisabled = isChecked && selectedCount === 1;
              return (
                <FormControlLabel
                  key={device.key}
                  sx={FORM_CONTROL_ROW_CHECKBOX_SX}
                  control={
                    <Checkbox
                      checked={isChecked}
                      disabled={isDisabled || disabled}
                      onChange={(e) =>
                        onDeviceChange(device.key, e.target.checked)
                      }
                    />
                  }
                  label={device.label}
                />
              );
            })}
          </FormGroup>

          <Box mt={3}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t(PLATFORMS_PLACEHOLDER)}
            </Typography>
            <FormGroup
              row
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              {PLATFORMS.map((platform) => {
                const isChecked = localState.platforms.includes(platform.key);
                const selectedCount = localState.platforms.length;
                const isDisabled = isChecked && selectedCount === 1;
                return (
                  <FormControlLabel
                    key={platform.key}
                    sx={FORM_CONTROL_ROW_CHECKBOX_SX}
                    control={
                      <Checkbox
                        checked={isChecked}
                        disabled={isDisabled || disabled}
                        onChange={(e) =>
                          onPlatformChange(platform.key, e.target.checked)
                        }
                      />
                    }
                    label={platform.label}
                  />
                );
              })}
            </FormGroup>
          </Box>

          <Box mt={3}>
            <Typography variant="subtitle1">
              {t(PLACEMENTS_PLACEHOLDER)}
            </Typography>
            {error?.placements?.message && (
              <Typography variant="caption" color="error" sx={{ mb: 1 }}>
                {error?.placements?.message}
              </Typography>
            )}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {PLACEMENT_GROUPS.map((group) => {
                const visiblePlacements = group.placements.filter((placement) =>
                  localState.platforms.includes(placement.platform)
                );

                if (visiblePlacements.length === 0) {
                  return null;
                }

                const groupPlacementKeys = visiblePlacements.map((p) => p.key);
                const selectedInGroup = localState.placements.filter((key) =>
                  groupPlacementKeys.includes(key)
                );
                const hasAnySelected = selectedInGroup.length > 0;

                return (
                  <Grid item xs={12} md={6} key={group.groupKey}>
                    <Accordion
                      title={group.groupLabel}
                      actionsComponent={
                        <Checkbox
                          checked={hasAnySelected}
                          disabled={disabled}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            e?.stopPropagation();
                            onGroupPlacementsChange(group, e.target.checked);
                          }}
                        />
                      }
                      sx={{
                        mb: 2,
                        border: `1px solid ${INPUT_COLOR_BORDER}`,
                        borderRadius: 2,
                        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
                      }}
                    >
                      <FormGroup orientation="vertical">
                        {visiblePlacements.map((placement) => {
                          const isChecked = localState.placements.includes(
                            placement.key
                          );
                          return (
                            <FormControlLabel
                              key={placement.key}
                              sx={FORM_CONTROL_STACK_CHECKBOX_SX}
                              control={
                                <Checkbox
                                  checked={isChecked}
                                  disabled={disabled}
                                  onChange={(e) =>
                                    onPlacementChange(
                                      placement,
                                      e.target.checked
                                    )
                                  }
                                />
                              }
                              label={placement.label}
                            />
                          );
                        })}
                      </FormGroup>
                    </Accordion>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};
